import React, {useCallback, useEffect, useEffectEvent, useState} from 'react';
import type {BaseTextFieldProps} from '@mui/material/TextField/TextField';
import {DesktopDatePicker, LocalizationProvider} from '@mui/x-date-pickers-pro';
import {AdapterDateFns} from '@mui/x-date-pickers-pro/AdapterDateFns';
import {elGR, nbNO} from '@mui/x-date-pickers-pro/locales';
import {format, parse} from 'date-fns';
import {nb} from 'date-fns/locale/nb';
import uniqueId from 'lodash/uniqueId';
import type {Locale} from 'localization/types';
import type {FieldRequiredSimple} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {Layout} from 'components/layout';
import type {BaseFieldProps} from './types';
import {CalendarIcon} from '../icons';
import {FieldLabel} from './FieldLabel';
import {NullFieldPlaceholder} from './NullFieldPlaceholder';

type Props = BaseFieldProps & {
  /** Date as string, e.g. "2021-12-31", or null */
  value?: string | null;
  required?: FieldRequiredSimple;
  name: string;
  onChange: (value: string | null) => void;
  onBlur?: () => void;
};

const dateLocaleMap: Record<Locale, typeof nbNO> = {
  nb: nbNO,
  nn: nbNO,
  en: elGR,
};

export const DatePickerField: React.FC<Props> = ({
  name,
  label,
  value,
  readonly,
  showWhenReadonlyAndEmpty = false,
  required = false,
  error,
  layout,
  onChange,
  onBlur,
}) => {
  const {locale = 'nb'} = useLocalization();
  const [localValue, setLocalValue] = useState<Date | null>(null);
  const onSetLocalValue = useEffectEvent(setLocalValue);

  const id = uniqueId(`datefield-${name}-`);

  const handleChange = useCallback(
    (newValue: Date | null) => {
      // DatePicker does not have a onBlur event, so we attach it to onChange.
      onBlur && onBlur();
      try {
        setLocalValue(newValue);
        onChange(dateToString(newValue));
      } catch {
        onChange('Invalid date'); // Will fail date check by validator.
      }
    },
    [onBlur, onChange],
  );

  // When  value changes from outside
  // => update local value
  useEffect(() => {
    if (value !== dateToString(localValue)) {
      onSetLocalValue(stringToDate(value));
    }
  }, [value, localValue, onBlur]);

  const ariaInvalid = error === true || (required === true && !localValue);
  const color: BaseTextFieldProps['color'] =
    error === 'warning'
      ? 'warning'
      : error === true || ariaInvalid
        ? 'error'
        : undefined;

  return (
    <Layout {...layout}>
      {label && (
        <FieldLabel
          label={label}
          required={required === true}
          error={error}
          htmlFor={id}
        />
      )}
      {readonly && !value && !showWhenReadonlyAndEmpty ? (
        <NullFieldPlaceholder />
      ) : (
        <LocalizationProvider
          // We don't use dayjs here, because it does not handle year 1-99.
          dateAdapter={AdapterDateFns}
          adapterLocale={nb}
          localeText={
            dateLocaleMap[locale].components.MuiLocalizationProvider
              .defaultProps.localeText
          }>
          <DesktopDatePicker<false>
            enableAccessibleFieldDOMStructure={false}
            aria-invalid={ariaInvalid}
            value={localValue}
            disabled={!!readonly}
            required={required === true}
            error={error === true}
            color={color}
            // Something wrong with the IDE. A Date object is returned as value, as the doc states.
            // But the IDE thinks it is a PickerValue.
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            onChange={handleChange}
            slotProps={{
              openPickerIcon: {
                color: readonly ? 'disabled' : 'primary',
              },
              field: params => {
                return {
                  id: id,
                  color: color,
                  required: required === true,
                  disabled: params.isPickerDisabled,
                  'aria-invalid': ariaInvalid,
                };
              },

              textField: params => {
                return {
                  ...params,
                  inputProps: {
                    color: color,
                    required: required === true,
                    disabled: !!readonly,
                    'aria-invalid': ariaInvalid,
                  },
                };
              },
            }}
            slots={{
              openPickerIcon: CalendarIcon,
            }}
          />
        </LocalizationProvider>
      )}
    </Layout>
  );
};

const dateToString = (date: Date | null | undefined): string | null => {
  try {
    return date ? format(date, 'yyyy-MM-dd') : null;
  } catch {
    return 'Invalid date';
  }
};

const stringToDate = (string: string | null | undefined): Date | null => {
  try {
    return string ? parse(string, 'yyyy-MM-dd', new Date()) : null;
  } catch {
    return null;
  }
};
